'use client'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Text from '@mui/material/Typography'
import React from 'react'

import DownloadGuide from '@/components/DownloadGuide'
import InteractiveMap from '@/components/EtsMap/InteractiveMap'
import Link from '@/components/Link'
import EtsFAQ from '@/sections/EtsFAQ'
import { HUBSPOT_FORM_IDS } from '@/utills/constants'

function CarrierLink({ children, href }: { children: React.ReactNode; href: string }) {
    return (
        <Link sx={{ textDecoration: 'underline' }} href={href} color={'Grey600'} newPage>
            {children}
        </Link>
    )
}

export default function Page() {
    return (
        <>
            <Stack spacing={1}>
                <Text variant={'h4'}>EU ETS surcharge explorer</Text>
                <Text color={'Grey600'} variant={'body3'}>
                    Explore how EU ETS surcharges vary across carriers and trade lanes, how it
                    compares with Lune’s own estimates, and get the answers to your EU ETS questions
                    below. Carrier surcharge data from{' '}
                    <CarrierLink href="https://www.cma-cgm.com/news/4498/launching-of-eu-emissions-trading-system-application-to-shipping">
                        CMA CGM
                    </CarrierLink>
                    ,{' '}
                    <CarrierLink href="https://coscoshipping.com.br/wp-content/uploads/2023/12/Notification-ETS-Surcharge-Carbon-Emissions-Trading-Systema-Surcharge-Effective-from-Jan-01-2024.pdf">
                        COSCO
                    </CarrierLink>
                    ,{' '}
                    <CarrierLink href="https://www.evergreen-line.com/latestnews/jsp/NEWS_NewsDetail.jsp?newsId=NEWS2023111300017011">
                        Evergreen
                    </CarrierLink>
                    ,{' '}
                    <CarrierLink href="https://www.hapag-lloyd.com/en/online-business/quotation/tariffs/trade-surcharges.html#anchor_76e716">
                        Hapag-Lloyd
                    </CarrierLink>
                    ,{' '}
                    <CarrierLink href="https://www.msc.com/en/newsroom/customer-advisories/2023/december/eu-emissions-trading-system-ets-update---23-december-2023">
                        MSC
                    </CarrierLink>
                    ,{' '}
                    <CarrierLink href="https://www.one-line-services.com.cn/sites/g/files/lnzjqr2191/files/2024-03/Customer%20Advisory%20of%20EU-ETS%20%28w.e.f%20April%201st%2C%202024%29.docx.pdf">
                        ONE
                    </CarrierLink>
                    . Last updated March 2024.
                </Text>
            </Stack>
            <InteractiveMap />
            <Text variant={'body1'}>
                <b>The takeaway</b>
                <br />
                <br />
                <br />
                <b>Carriers overcharge 2–3×:</b> every carrier charges more than the EU ETS
                surcharge should be, according to Lune’s estimates.
                <br />
                <br />
                Charging €5–20 extra per TEU seems marginal, especially considering the total cost
                of the shipment. However, it adds up!
                <br />
                <br />
                For example:
                <ul>
                    <li>
                        COSCO Hope @ 13,000 TEU capacity: <b>€440,700 profit</b>
                    </li>
                    <li>
                        CMA CGA Galapagos @15,000 TEU capacity: <b>€147,300 profit</b>
                    </li>
                    <li>
                        MSC Fiammetta @ 5,760 TEU capacity: <b>€58,060 profit</b>
                    </li>
                </ul>
                Note: these are the profits <b>after</b> paying the EU ETS surcharge estimated by
                Lune for each <b>one-way voyage!</b>
                <br />
                <br />
                This may not be fully profiteering, but the carriers may also be adding a buffer to
                account for administrative work, EUA price fluctuations (which can be high), or
                simply applying a different calculation method (e.g. charging for 100% of emissions
                instead of the 40% required by the EU ETS).
            </Text>
            <Divider />
            <EtsFAQ />
            <DownloadGuide purpleVersion={true} hubspotFormId={HUBSPOT_FORM_IDS.purpleForm} />
        </>
    )
}
